import React, { memo, ComponentProps, ReactNode } from 'react'

import styled from '@emotion/styled'

import { ExpandedContentWrapper } from '../../components/ExpandableCard'

import { Box } from './Box'
import { DropdownIcon } from './DropdownIcon'
import { FlexBox } from './FlexBox'
import { Icon } from './Icon'
import { ListItem } from './ListItem'

const StyledContentWrapper = styled(ExpandedContentWrapper)`
  border-bottom-left-radius: inherit;
  border-bottom-right-radius: inherit;
`

const ExpandedContentContainer = styled(Box)`
  border-radius: inherit;
`

interface ExpandableListItemProps
  extends Omit<ComponentProps<typeof ListItem>, 'content' | 'clickable'> {
  content: ReactNode
  isExpanded?: boolean
  shouldExpandOnContainerClick?: boolean
  contentProps?: ComponentProps<typeof FlexBox>
  withBackground?: boolean
  withArrow?: boolean
}

export const ExpandableListItem = memo(
  ({
    children,
    isExpanded,
    content,
    iconProps,
    shouldExpandOnContainerClick = true,
    cursor = shouldExpandOnContainerClick ? 'pointer' : 'auto',
    onClick,
    contentProps,
    withBackground = true,
    withArrow = true,
    ...rest
  }: ExpandableListItemProps) => {
    return (
      <ListItem
        {...{
          onClick: shouldExpandOnContainerClick ? onClick : undefined,
          cursor,
          clickable: shouldExpandOnContainerClick,
          fd: 'column',
          px: 'abs.0',
          pb: 'abs.0',
          withHoverEffect: !isExpanded,
          ...rest,
        }}>
        <FlexBox
          {...{
            fg: 1,
            ai: 'center',
            gap: 'md',
            w: '100%',
            px: 'md',
            pb: 'md',
          }}>
          {iconProps ? <Icon {...iconProps} /> : null}
          <FlexBox {...{ flex: 1, ai: 'center', ...contentProps }}>
            {content}
          </FlexBox>
          {withArrow ? (
            <Box
              {...{
                onClick: !shouldExpandOnContainerClick ? onClick : undefined,
                role: 'button',
                'aria-label': 'Expand content',
              }}>
              <DropdownIcon {...{ isExpanded }} />
            </Box>
          ) : null}
        </FlexBox>

        {isExpanded ? (
          <StyledContentWrapper
            {...{
              pl: 'abs.0',
              onClick: (e) => e.stopPropagation(),
              bgc: withBackground ? 'background' : 'transparent',
            }}>
            <ExpandedContentContainer
              {...{ bgc: withBackground ? 'transparent.50' : 'transparent' }}>
              {children}
            </ExpandedContentContainer>
          </StyledContentWrapper>
        ) : null}
      </ListItem>
    )
  },
)

export const NestedListItem = (props: ComponentProps<typeof ListItem>) => (
  <ListItem {...{ cursor: 'auto', bgc: 'transparent', ...props }} />
)
